import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import GroupsIcon from '@mui/icons-material/Groups';
import BusinessIcon from '@mui/icons-material/Business';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';
import './index.css';
import {
  GET_FOUNDERS_CIRCLE_DATA,
  UPDATE_FOUNDERS_CIRCLE_DATA,
  DELETE_FOUNDERS_CIRCLE_DATA,
} from '../../api/foundersCircle.js';

const STATUS_OPTIONS = [
  'Pending',
  'Under Review',
  'Contacted',
  'Accepted',
  'Rejected',
];

const STAGE_OPTIONS = [
  'Pre-seed / Ideation',
  'Early traction (Seed)',
  'Growth (Series A/B)',
  'Scale / Institution',
];

const SECTOR_OPTIONS = [
  'B2B SaaS',
  'Fintech',
  'Healthcare',
  'Consumer Tech',
  'Enterprise / AI',
  'Other',
];

const BUSINESS_MODEL_OPTIONS = [
  'B2B Subscription',
  'Transactional / Marketplace',
  'Usage-based',
  'Enterprise contract',
  'Consumer D2C',
];

const FOCUS_AREA_OPTIONS = [
  'Hiring & executive leadership',
  'Fundraising & board dynamics',
  'Founder psychology & resilience',
  'Pricing & commercial motion',
  'Market expansion & GTM',
  'Product strategy & focus',
  'Scaling operations',
  'Co-founder & team dynamics',
];

const TIME_COMMITMENT_OPTIONS = [
  '2 hours/month (session only)',
  '3–4 hours/month (session + async exchange)',
  '5+ hours/month (deep peer involvement)',
];

const FoundersCircleAdminView = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // View modal state
  const [viewItem, setViewItem] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);

  // Edit modal state
  const [editItem, setEditItem] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete modal state
  const [deleteId, setDeleteId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(GET_FOUNDERS_CIRCLE_DATA);
      if (res.data && Array.isArray(res.data.data)) {
        setData(res.data.data);
      } else if (Array.isArray(res.data)) {
        setData(res.data);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error('Error fetching Founders Circle submissions:', err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filtered list
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchesSearch =
        searchTerm === '' ||
        (item.founderName &&
          item.founderName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.companyName &&
          item.companyName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.workEmail &&
          item.workEmail.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.sector &&
          item.sector.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.cityCountry &&
          item.cityCountry.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.currentStage &&
          item.currentStage.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.businessModel &&
          item.businessModel.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus =
        statusFilter === 'All' || item.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [data, searchTerm, statusFilter]);

  // Statistics
  const stats = useMemo(() => {
    const total = data.length;
    const pending = data.filter((d) => d.status === 'Pending' || !d.status).length;
    const underReview = data.filter((d) => d.status === 'Under Review').length;
    const accepted = data.filter((d) => d.status === 'Accepted').length;
    return { total, pending, underReview, accepted };
  }, [data]);

  // Handlers for View
  const handleOpenView = (item) => {
    setViewItem(item);
    setViewOpen(true);
  };

  const handleCloseView = () => {
    setViewOpen(false);
    setViewItem(null);
  };

  // Handlers for Edit
  const handleOpenEdit = (item) => {
    setEditItem({
      _id: item._id,
      founderName: item.founderName || '',
      roleTitle: item.roleTitle || '',
      workEmail: item.workEmail || '',
      phone: item.phone || '',
      linkedIn: item.linkedIn || '',
      cityCountry: item.cityCountry || '',
      companyName: item.companyName || '',
      website: item.website || '',
      yearFounded: item.yearFounded || '',
      sector: item.sector || '',
      oneLineDescription: item.oneLineDescription || '',
      currentStage: item.currentStage || '',
      businessModel: item.businessModel || '',
      monthlyRevenue: item.monthlyRevenue || '',
      teamSize: item.teamSize || '',
      capitalRaised: item.capitalRaised || '',
      growthRate: item.growthRate || '',
      currentPriorities: item.currentPriorities || '',
      biggestChallenge: item.biggestChallenge || '',
      selectedFocusAreas: Array.isArray(item.selectedFocusAreas) ? item.selectedFocusAreas : [],
      selectedTimeCommitment: item.selectedTimeCommitment || '',
      peerStagePreference: item.peerStagePreference || '',
      avoidCompetitors: item.avoidCompetitors || '',
      status: item.status || 'Pending',
      adminNotes: item.adminNotes || '',
    });
    setEditError('');
    setEditOpen(true);
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditItem(null);
    setEditError('');
  };

  const handleEditChange = (field, value) => {
    setEditItem((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async () => {
    if (!editItem || !editItem._id) return;
    setEditLoading(true);
    setEditError('');

    try {
      const updateUrl = `${UPDATE_FOUNDERS_CIRCLE_DATA}/${editItem._id}`;
      const payload = {
        founderName: editItem.founderName,
        roleTitle: editItem.roleTitle,
        workEmail: editItem.workEmail,
        phone: editItem.phone,
        linkedIn: editItem.linkedIn,
        cityCountry: editItem.cityCountry,
        companyName: editItem.companyName,
        website: editItem.website,
        yearFounded: editItem.yearFounded,
        sector: editItem.sector,
        oneLineDescription: editItem.oneLineDescription,
        currentStage: editItem.currentStage,
        businessModel: editItem.businessModel,
        monthlyRevenue: editItem.monthlyRevenue,
        teamSize: editItem.teamSize,
        capitalRaised: editItem.capitalRaised,
        growthRate: editItem.growthRate,
        currentPriorities: editItem.currentPriorities,
        biggestChallenge: editItem.biggestChallenge,
        selectedFocusAreas: editItem.selectedFocusAreas,
        selectedTimeCommitment: editItem.selectedTimeCommitment,
        peerStagePreference: editItem.peerStagePreference,
        avoidCompetitors: editItem.avoidCompetitors,
        status: editItem.status,
        adminNotes: editItem.adminNotes,
      };

      const res = await axios.put(updateUrl, payload);
      const updated = res.data.data;

      setData((prev) =>
        prev.map((item) => (item._id === updated._id ? updated : item))
      );

      if (viewItem && viewItem._id === updated._id) {
        setViewItem(updated);
      }

      handleCloseEdit();
    } catch (err) {
      console.error('Failed to update Founders Circle entry:', err);
      setEditError(
        err.response?.data?.details ||
          err.response?.data?.error ||
          'Failed to save changes. Please check required fields.'
      );
    } finally {
      setEditLoading(false);
    }
  };

  // Handlers for Delete
  const handleOpenDelete = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteOpen(false);
    setDeleteId(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${DELETE_FOUNDERS_CIRCLE_DATA}/${deleteId}`);
      setData((prev) => prev.filter((item) => item._id !== deleteId));
      if (viewItem && viewItem._id === deleteId) {
        handleCloseView();
      }
      handleCloseDelete();
    } catch (err) {
      console.error('Failed to delete Founders Circle application:', err);
      alert('Failed to delete application. Please try again.');
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (data.length === 0) {
      alert('No data to export.');
      return;
    }

    const headers = [
      'ID',
      'Founder Name',
      'Role Title',
      'Work Email',
      'Phone',
      'LinkedIn',
      'City & Country',
      'Company Name',
      'Website',
      'Year Founded',
      'Sector',
      'One Line Description',
      'Current Stage',
      'Business Model',
      'Monthly Revenue (USD)',
      'Team Size',
      'Capital Raised (USD)',
      'Growth Rate',
      'Current Priorities',
      'Biggest Challenge',
      'Selected Focus Areas',
      'Time Commitment',
      'Peer Stage Preference',
      'Avoid Competitors',
      'Status',
      'Admin Notes',
      'Submitted Date',
    ];

    const rows = data.map((item) => [
      `"${item._id || ''}"`,
      `"${(item.founderName || '').replace(/"/g, '""')}"`,
      `"${(item.roleTitle || '').replace(/"/g, '""')}"`,
      `"${(item.workEmail || '').replace(/"/g, '""')}"`,
      `"${(item.phone || '').replace(/"/g, '""')}"`,
      `"${(item.linkedIn || '').replace(/"/g, '""')}"`,
      `"${(item.cityCountry || '').replace(/"/g, '""')}"`,
      `"${(item.companyName || '').replace(/"/g, '""')}"`,
      `"${(item.website || '').replace(/"/g, '""')}"`,
      `"${(item.yearFounded || '').replace(/"/g, '""')}"`,
      `"${(item.sector || '').replace(/"/g, '""')}"`,
      `"${(item.oneLineDescription || '').replace(/"/g, '""')}"`,
      `"${(item.currentStage || '').replace(/"/g, '""')}"`,
      `"${(item.businessModel || '').replace(/"/g, '""')}"`,
      `"${(item.monthlyRevenue || '').replace(/"/g, '""')}"`,
      `"${(item.teamSize || '').replace(/"/g, '""')}"`,
      `"${(item.capitalRaised || '').replace(/"/g, '""')}"`,
      `"${(item.growthRate || '').replace(/"/g, '""')}"`,
      `"${(item.currentPriorities || '').replace(/"/g, '""')}"`,
      `"${(item.biggestChallenge || '').replace(/"/g, '""')}"`,
      `"${(Array.isArray(item.selectedFocusAreas) ? item.selectedFocusAreas.join('; ') : '').replace(/"/g, '""')}"`,
      `"${(item.selectedTimeCommitment || '').replace(/"/g, '""')}"`,
      `"${(item.peerStagePreference || '').replace(/"/g, '""')}"`,
      `"${(item.avoidCompetitors || '').replace(/"/g, '""')}"`,
      `"${(item.status || 'Pending').replace(/"/g, '""')}"`,
      `"${(item.adminNotes || '').replace(/"/g, '""')}"`,
      `"${item.createdAt ? new Date(item.createdAt).toLocaleString() : ''}"`,
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `founders_circle_applications_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusChipColor = (status) => {
    switch (status) {
      case 'Accepted':
        return 'success';
      case 'Under Review':
        return 'info';
      case 'Contacted':
        return 'secondary';
      case 'Rejected':
        return 'error';
      case 'Pending':
      default:
        return 'warning';
    }
  };

  return (
    <Box className="founders-admin-wrapper">
      {/* 1. Header Section */}
      <Box className="founders-admin-header">
        <Box>
          <Typography variant="h4" className="founders-page-title">
            Founders Circle Applications
          </Typography>
          <Typography variant="body2" className="founders-page-subtitle">
            Manage, review and match founder peer circles
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            className="founders-btn-refresh"
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportCSV}
            className="founders-btn-export"
          >
            Export to CSV
          </Button>
        </Box>
      </Box>

      {/* 2. Stat Cards Grid */}
      <Grid container spacing={3} className="founders-stats-grid">
        <Grid item xs={12} sm={6} md={3}>
          <Box className="founders-stat-card">
            <Box className="founders-stat-icon stat-icon-total">
              <GroupsIcon />
            </Box>
            <Box className="founders-stat-info">
              <Typography className="founders-stat-value">
                {stats.total}
              </Typography>
              <Typography className="founders-stat-label">
                Total Applications
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box className="founders-stat-card">
            <Box className="founders-stat-icon stat-icon-pending">
              <HourglassEmptyIcon />
            </Box>
            <Box className="founders-stat-info">
              <Typography className="founders-stat-value">
                {stats.pending}
              </Typography>
              <Typography className="founders-stat-label">
                Pending Review
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box className="founders-stat-card">
            <Box className="founders-stat-icon stat-icon-review">
              <BusinessIcon />
            </Box>
            <Box className="founders-stat-info">
              <Typography className="founders-stat-value">
                {stats.underReview}
              </Typography>
              <Typography className="founders-stat-label">
                Under Review
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box className="founders-stat-card">
            <Box className="founders-stat-icon stat-icon-accepted">
              <CheckCircleOutlineIcon />
            </Box>
            <Box className="founders-stat-info">
              <Typography className="founders-stat-value">
                {stats.accepted}
              </Typography>
              <Typography className="founders-stat-label">
                Accepted Members
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* 3. Controls & Filter Bar */}
      <Paper className="founders-controls-paper">
        <TextField
          placeholder="Search by founder, company, email, sector, stage..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="founders-search-box"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          label="Status Filter"
          variant="outlined"
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="founders-filter-box"
        >
          <MenuItem value="All">All Statuses ({data.length})</MenuItem>
          {STATUS_OPTIONS.map((opt) => {
            const count = data.filter((d) => d.status === opt).length;
            return (
              <MenuItem key={opt} value={opt}>
                {opt} ({count})
              </MenuItem>
            );
          })}
        </TextField>
      </Paper>

      {/* 4. Applications Data Table */}
      <Paper className="founders-table-paper">
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead className="founders-table-header">
              <TableRow>
                <TableCell>Founder & Role</TableCell>
                <TableCell>Company & Sector</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Stage & Model</TableCell>
                <TableCell>Focus Areas</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={36} sx={{ color: '#5c4eb4' }} />
                    <Typography variant="body2" sx={{ mt: 1.5, color: '#64748b' }}>
                      Loading applications...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="founders-empty-state">
                    <Typography variant="subtitle1" fontWeight={600}>
                      No applications found
                    </Typography>
                    <Typography variant="body2">
                      Try adjusting your search criteria or filter.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((item) => (
                  <TableRow key={item._id} className="founders-table-row">
                    <TableCell>
                      <Typography fontWeight={600} color="#0f172a">
                        {item.founderName}
                      </Typography>
                      <Typography className="founders-sub-text">
                        {item.roleTitle || 'Founder'}
                      </Typography>
                      <Typography className="founders-sub-text">
                        {item.workEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600} color="#0f172a">
                        {item.companyName}
                      </Typography>
                      <Typography className="founders-sub-text">
                        {item.sector}
                      </Typography>
                      {item.website && (
                        <a
                          href={
                            item.website.startsWith('http')
                              ? item.website
                              : `https://${item.website}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="founders-link-text"
                          style={{ fontSize: '11.5px' }}
                        >
                          Visit Website ↗
                        </a>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.cityCountry}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.currentStage || 'Not specified'}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: '11px',
                          bgcolor: '#f1f5f9',
                          color: '#334155',
                          mb: 0.5,
                        }}
                      />
                      <Typography className="founders-sub-text">
                        {item.businessModel}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      {Array.isArray(item.selectedFocusAreas) &&
                      item.selectedFocusAreas.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {item.selectedFocusAreas.slice(0, 2).map((fa) => (
                            <Chip
                              key={fa}
                              label={fa}
                              size="small"
                              className="founders-chip-focus"
                            />
                          ))}
                          {item.selectedFocusAreas.length > 2 && (
                            <Tooltip
                              title={item.selectedFocusAreas.slice(2).join(', ')}
                            >
                              <Chip
                                label={`+${item.selectedFocusAreas.length - 2} more`}
                                size="small"
                                className="founders-chip-focus"
                                sx={{ bgcolor: '#e2e8f0 !important' }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      ) : (
                        <Typography className="founders-sub-text">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status || 'Pending'}
                        color={getStatusChipColor(item.status)}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: '11.5px' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '12px', color: '#64748b' }}>
                        {item.createdAt
                          ? new Date(item.createdAt).toLocaleDateString()
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenView(item)}
                            sx={{ color: '#5c4eb4' }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit / Review">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEdit(item)}
                            sx={{ color: '#0284c7' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDelete(item._id)}
                            sx={{ color: '#ef4444' }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 5. View Details Modal */}
      <Dialog
        open={viewOpen}
        onClose={handleCloseView}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            pb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="#0f172a">
              {viewItem?.founderName}
            </Typography>
            <Typography variant="body2" color="#64748b">
              {viewItem?.companyName} · {viewItem?.sector}
            </Typography>
          </Box>
          <Chip
            label={viewItem?.status || 'Pending'}
            color={getStatusChipColor(viewItem?.status)}
            sx={{ fontWeight: 700 }}
          />
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {viewItem && (
            <Box>
              {/* Section 1: Founder & Company Profile */}
              <Box className="founders-detail-section">
                <Typography className="founders-section-title">
                  Founder & Company Profile
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Full Name</span>
                      <span className="founders-detail-val">{viewItem.founderName}</span>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Role / Title</span>
                      <span className="founders-detail-val">{viewItem.roleTitle}</span>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Work Email</span>
                      <span className="founders-detail-val">{viewItem.workEmail}</span>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Phone / WhatsApp</span>
                      <span className="founders-detail-val">{viewItem.phone || 'Not provided'}</span>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">LinkedIn Profile</span>
                      <span className="founders-detail-val">
                        {viewItem.linkedIn ? (
                          <a
                            href={
                              viewItem.linkedIn.startsWith('http')
                                ? viewItem.linkedIn
                                : `https://${viewItem.linkedIn}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="founders-link-text"
                          >
                            {viewItem.linkedIn}
                          </a>
                        ) : (
                          'Not provided'
                        )}
                      </span>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">City & Country</span>
                      <span className="founders-detail-val">{viewItem.cityCountry}</span>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Company Name</span>
                      <span className="founders-detail-val">{viewItem.companyName}</span>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Website</span>
                      <span className="founders-detail-val">
                        {viewItem.website ? (
                          <a
                            href={
                              viewItem.website.startsWith('http')
                                ? viewItem.website
                                : `https://${viewItem.website}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="founders-link-text"
                          >
                            {viewItem.website}
                          </a>
                        ) : (
                          'Not provided'
                        )}
                      </span>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Year Founded</span>
                      <span className="founders-detail-val">{viewItem.yearFounded || 'Not provided'}</span>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Sector</span>
                      <span className="founders-detail-val">{viewItem.sector}</span>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">One-Line Company Description</span>
                      <Box className="founders-detail-val-box">
                        {viewItem.oneLineDescription}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Section 2: Stage, Scale & Operating Priorities */}
              <Box className="founders-detail-section">
                <Typography className="founders-section-title">
                  Stage, Scale & Priorities
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Current Stage</span>
                      <span className="founders-detail-val">{viewItem.currentStage}</span>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Business Model</span>
                      <span className="founders-detail-val">{viewItem.businessModel}</span>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Monthly Revenue (USD)</span>
                      <span className="founders-detail-val">{viewItem.monthlyRevenue ? `$${viewItem.monthlyRevenue}` : 'Not disclosed'}</span>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Full-Time Team Size</span>
                      <span className="founders-detail-val">{viewItem.teamSize || 'Not disclosed'}</span>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Capital Raised (USD)</span>
                      <span className="founders-detail-val">{viewItem.capitalRaised ? `$${viewItem.capitalRaised}` : 'Not disclosed'}</span>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Growth Rate</span>
                      <span className="founders-detail-val">{viewItem.growthRate || 'Not disclosed'}</span>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Top Priorities (Next 90 Days)</span>
                      <Box className="founders-detail-val-box">
                        {viewItem.currentPriorities}
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Biggest Operating Challenge</span>
                      <Box className="founders-detail-val-box">
                        {viewItem.biggestChallenge}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Section 3: Preferences & Trust */}
              <Box className="founders-detail-section">
                <Typography className="founders-section-title">
                  Circle Preferences & Trust
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Selected Focus Areas</span>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                        {Array.isArray(viewItem.selectedFocusAreas) &&
                          viewItem.selectedFocusAreas.map((fa) => (
                            <Chip
                              key={fa}
                              label={fa}
                              size="small"
                              sx={{
                                bgcolor: '#ede9fe',
                                color: '#5c4eb4',
                                fontWeight: 600,
                              }}
                            />
                          ))}
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Monthly Time Commitment</span>
                      <span className="founders-detail-val">{viewItem.selectedTimeCommitment || '3–4 hours/month'}</span>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Competitors / Conflicts to Avoid</span>
                      <span className="founders-detail-val">{viewItem.avoidCompetitors || 'None specified'}</span>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Circle Commitments</span>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                        <span className="founders-agreement-badge">
                          ✓ Strict Confidentiality
                        </span>
                        <span className="founders-agreement-badge">
                          ✓ Radical Candour & Authenticity
                        </span>
                        <span className="founders-agreement-badge">
                          ✓ No Selling or Pitching
                        </span>
                        <span className="founders-agreement-badge">
                          ✓ Accurate Application Declaration
                        </span>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Section 4: Admin Review Notes */}
              <Box className="founders-detail-section">
                <Typography className="founders-section-title">
                  Admin Review & Notes
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Status</span>
                      <Chip
                        label={viewItem.status || 'Pending'}
                        color={getStatusChipColor(viewItem.status)}
                        size="small"
                        sx={{ fontWeight: 700, width: 'fit-content', mt: 0.5 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Submitted On</span>
                      <span className="founders-detail-val">
                        {viewItem.createdAt
                          ? new Date(viewItem.createdAt).toLocaleString()
                          : '—'}
                      </span>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box className="founders-detail-row">
                      <span className="founders-detail-label">Internal Admin Notes</span>
                      <Box className="founders-detail-val-box">
                        {viewItem.adminNotes || 'No notes added yet.'}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, bgcolor: '#f8fafc' }}>
          <Button onClick={handleCloseView} sx={{ color: '#64748b' }}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              handleCloseView();
              handleOpenEdit(viewItem);
            }}
            className="founders-btn-export"
          >
            Edit / Review
          </Button>
        </DialogActions>
      </Dialog>

      {/* 6. Edit / Review Modal */}
      <Dialog
        open={editOpen}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle
          sx={{
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            pb: 2,
          }}
        >
          <Typography variant="h6" fontWeight={700} color="#0f172a">
            Edit / Review Application
          </Typography>
          <Typography variant="body2" color="#64748b">
            Update application details, admission status and internal notes
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {editError && (
            <Box
              sx={{
                mb: 2.5,
                p: 1.5,
                bgcolor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 1.5,
                color: '#b91c1c',
                fontSize: 13,
              }}
            >
              {Array.isArray(editError) ? editError.join(', ') : editError}
            </Box>
          )}

          {editItem && (
            <Grid container spacing={2.5}>
              {/* Admin Controls */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Admission Status *"
                  value={editItem.status}
                  onChange={(e) => handleEditChange('status', e.target.value)}
                  size="small"
                >
                  {STATUS_OPTIONS.map((st) => (
                    <MenuItem key={st} value={st}>
                      {st}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Founder Full Name *"
                  value={editItem.founderName}
                  onChange={(e) => handleEditChange('founderName', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Internal Admin Notes"
                  multiline
                  rows={2}
                  value={editItem.adminNotes}
                  onChange={(e) => handleEditChange('adminNotes', e.target.value)}
                  size="small"
                  placeholder="Add cohort matching recommendations, interview feedback, etc."
                />
              </Grid>

              {/* Profile Details */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Role / Title *"
                  value={editItem.roleTitle}
                  onChange={(e) => handleEditChange('roleTitle', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Work Email *"
                  value={editItem.workEmail}
                  onChange={(e) => handleEditChange('workEmail', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone / WhatsApp"
                  value={editItem.phone}
                  onChange={(e) => handleEditChange('phone', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="LinkedIn Profile URL"
                  value={editItem.linkedIn}
                  onChange={(e) => handleEditChange('linkedIn', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Name *"
                  value={editItem.companyName}
                  onChange={(e) => handleEditChange('companyName', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City & Country *"
                  value={editItem.cityCountry}
                  onChange={(e) => handleEditChange('cityCountry', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Website URL"
                  value={editItem.website}
                  onChange={(e) => handleEditChange('website', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Sector *"
                  value={editItem.sector}
                  onChange={(e) => handleEditChange('sector', e.target.value)}
                  size="small"
                >
                  {SECTOR_OPTIONS.map((sec) => (
                    <MenuItem key={sec} value={sec}>
                      {sec}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="One-Line Company Description *"
                  multiline
                  rows={2}
                  value={editItem.oneLineDescription}
                  onChange={(e) => handleEditChange('oneLineDescription', e.target.value)}
                  size="small"
                />
              </Grid>

              {/* Stage & Traction */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Current Stage *"
                  value={editItem.currentStage}
                  onChange={(e) => handleEditChange('currentStage', e.target.value)}
                  size="small"
                >
                  {STAGE_OPTIONS.map((stg) => (
                    <MenuItem key={stg} value={stg}>
                      {stg}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Business Model *"
                  value={editItem.businessModel}
                  onChange={(e) => handleEditChange('businessModel', e.target.value)}
                  size="small"
                >
                  {BUSINESS_MODEL_OPTIONS.map((bm) => (
                    <MenuItem key={bm} value={bm}>
                      {bm}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Monthly Revenue (USD)"
                  value={editItem.monthlyRevenue}
                  onChange={(e) => handleEditChange('monthlyRevenue', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Team Size"
                  value={editItem.teamSize}
                  onChange={(e) => handleEditChange('teamSize', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Total Capital Raised (USD)"
                  value={editItem.capitalRaised}
                  onChange={(e) => handleEditChange('capitalRaised', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Growth Rate"
                  value={editItem.growthRate}
                  onChange={(e) => handleEditChange('growthRate', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Top Priorities (90 Days) *"
                  multiline
                  rows={2}
                  value={editItem.currentPriorities}
                  onChange={(e) => handleEditChange('currentPriorities', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Biggest Operating Challenge *"
                  multiline
                  rows={2}
                  value={editItem.biggestChallenge}
                  onChange={(e) => handleEditChange('biggestChallenge', e.target.value)}
                  size="small"
                />
              </Grid>

              {/* Preferences */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Monthly Time Commitment"
                  value={editItem.selectedTimeCommitment}
                  onChange={(e) => handleEditChange('selectedTimeCommitment', e.target.value)}
                  size="small"
                >
                  {TIME_COMMITMENT_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Avoid Competitors"
                  value={editItem.avoidCompetitors}
                  onChange={(e) => handleEditChange('avoidCompetitors', e.target.value)}
                  size="small"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, bgcolor: '#f8fafc' }}>
          <Button onClick={handleCloseEdit} disabled={editLoading} sx={{ color: '#64748b' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={editLoading}
            className="founders-btn-export"
          >
            {editLoading ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 7. Delete Confirmation Modal */}
      <Dialog open={deleteOpen} onClose={handleCloseDelete} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>
          Delete Application?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="#64748b">
            Are you sure you want to delete this Founder Circles application? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDelete} sx={{ color: '#64748b' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FoundersCircleAdminView;
